import React, { useState, useCallback, useEffect, useRef } from 'react';
import { ArrowDown } from 'lucide-react';
import { PlanProgressIndicator } from './plan/PlanProgressIndicator';
import { MessageList } from './chat/MessageList';
import { PromptInput } from './chat/PromptInput';
import { VoiceOverlay } from './voice/VoiceOverlay';
import type { UIMessage, ChatStatus } from '../types';
import type {
  PlanningStrategy,
  OrchestratedStatusDict,
  ResearchStatusDict,
} from '../types/planning';
import type { UploadState } from '../hooks/useFileUpload';
import type { FileInfo } from '../hooks/useFileList';

type PlanStatusType = OrchestratedStatusDict | ResearchStatusDict | null;

interface FileUploadHook {
  uploads: UploadState[];
  addFiles: (files: FileList | File[]) => Promise<void>;
  cancelUpload: (uploadId: string) => void;
  cancelAll: () => void;
  clearCompleted: () => void;
  isUploading: boolean;
  totalProgress: number;
}

interface ChatWindowProps {
  title?: string;
  messages: UIMessage[];
  status: ChatStatus;
  onSubmit: (text: string) => void;
  onStop?: () => void;
  onRetry?: () => void;
  onRegenerate?: () => void;
  planStrategy?: PlanningStrategy | null;
  planStatus?: PlanStatusType;
  isPlanActive?: boolean;
  /** Callback to open the plan sidebar panel */
  onOpenPlanPanel?: () => void;
  sessionId?: string;
  userId?: string;
  /** Spec 052 Step 9: lazy session bootstrap for voice-first flow. */
  onCreateSession?: () => Promise<string | null>;
  fileUpload?: FileUploadHook;
  /** Workspace files for @-mention autocomplete */
  workspaceFiles?: FileInfo[];
  filesSidebarOpen?: boolean;
  toolMetadata?: Map<string, {
    startedAt?: number;
    completedAt?: number;
    durationMs?: number;
    category?: string;
    serverName?: string;
    parentToolCallId?: string;
  }>;
  /** Timestamp of the last agent push message — triggers auto-scroll */
  lastPushTimestamp?: number;
  /** Last SSE/stream failure message for debugging */
  sendErrorDetail?: string | null;
  /** Whether the pre-first-message welcome greeting is still being fetched */
  welcomeMessageLoading?: boolean;
}

function ChatWindowInner({
  title,
  messages,
  status,
  onSubmit,
  onStop,
  onRetry,
  onRegenerate,
  planStrategy,
  planStatus,
  isPlanActive = false,
  onOpenPlanPanel,
  sessionId,
  userId,
  onCreateSession,
  fileUpload,
  workspaceFiles = [],
  filesSidebarOpen = true,
  toolMetadata,
  lastPushTimestamp = 0,
  sendErrorDetail,
  welcomeMessageLoading = false,
}: ChatWindowProps) {
  const [chatInput, setChatInput] = useState('');
  const [isDragOver, setIsDragOver] = useState(false);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const lastMessageCountRef = useRef(messages.length);
  const lastPushRef = useRef<number>(0);

  // Reset input when conversation changes
  useEffect(() => {
    setChatInput('');
  }, [sessionId]);

  const handleSubmit = useCallback(
    (text: string) => {
      onSubmit(text);
      setChatInput('');
    },
    [onSubmit],
  );

  // File upload functionality
  const fileUploadEnabled = !!(sessionId && userId && fileUpload);

  const handleFilesSelected = (files: FileList) => {
    if (fileUpload) {
      fileUpload.addFiles(files);
    }
  };

  // Drag and drop handlers
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (fileUploadEnabled) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    if (fileUploadEnabled && e.dataTransfer.files.length > 0) {
      fileUpload?.addFiles(e.dataTransfer.files);
    }
  };

  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior,
    });
  }, []);

  const handleScroll = useCallback(() => {
    if (!scrollRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    const atBottom = distanceFromBottom < 50;
    setIsAtBottom(atBottom);
    setShowScrollButton(distanceFromBottom > 100);
    if (atBottom) {
      setUnreadCount(0);
    }
  }, []);

  useEffect(() => {
    if (isAtBottom) {
      scrollToBottom('auto');
    }
  }, [messages, isAtBottom, scrollToBottom]);

  // Auto-scroll on agent push messages regardless of scroll position.
  // Listens for a DOM event dispatched by useChat when a push message arrives.
  // Uses setTimeout to wait for React to commit the new message to the DOM.
  useEffect(() => {
    const handler = () => {
      setTimeout(() => {
        scrollToBottom('smooth');
        setUnreadCount(0);
        setIsAtBottom(true);
      }, 300);
    };
    window.addEventListener('cxs-push-scroll', handler);
    return () => window.removeEventListener('cxs-push-scroll', handler);
  }, [scrollToBottom]);

  useEffect(() => {
    const delta = messages.length - lastMessageCountRef.current;
    if (delta > 0) {
      if (isAtBottom) {
        setUnreadCount(0);
      } else {
        setUnreadCount((prev) => prev + delta);
      }
    }
    if (delta < 0) {
      setUnreadCount(0);
    }
    lastMessageCountRef.current = messages.length;
  }, [messages.length, isAtBottom]);

  return (
    <div
      className={`flex-1 flex flex-col h-dvh min-h-0 bg-white dark:bg-gray-900 touch-pan-y relative ${
        isDragOver ? 'ring-2 ring-inset ring-sky-400' : ''
      }`}
      style={{ overscrollBehavior: 'contain' }}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Drag overlay */}
      {isDragOver && (
        <div className="absolute inset-0 bg-sky-50/80 z-50 flex items-center justify-center pointer-events-none">
          <div className="text-center">
            <svg className="w-16 h-16 text-sky-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            <p className="text-lg font-medium text-sky-700">Drop files to upload</p>
          </div>
        </div>
      )}

      {/* Header - not sticky, stays in flex flow */}
      {title && (
        <div className="flex-shrink-0 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-4 py-3 z-10">
          <div className="flex items-center justify-between max-w-4xl mx-auto">
            <h2 className="font-semibold text-gray-800 dark:text-gray-100 truncate">
              {title}
            </h2>
            <VoiceOverlay />
          </div>
        </div>
      )}

      {/* Messages Area */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto touch-pan-y relative"
        onScroll={handleScroll}
      >
        <div className="mx-auto max-w-4xl flex flex-col gap-4 px-2 py-4 md:gap-6 md:px-4">
          <MessageList
            messages={messages}
            status={status}
            sendErrorDetail={sendErrorDetail}
            onRetry={onRetry}
            onRegenerate={onRegenerate}
            userId={userId}
            toolMetadata={toolMetadata}
            welcomeMessageLoading={welcomeMessageLoading}
          />
        </div>
      </div>

      {unreadCount > 0 && !isAtBottom && (
        <button
          onClick={() => {
            scrollToBottom('smooth');
            setUnreadCount(0);
          }}
          className="absolute bottom-36 right-4 md:bottom-40 md:right-6 px-3 py-1.5
            bg-sky-600 text-white text-xs font-medium rounded-full shadow-lg
            hover:bg-sky-700 transition-colors z-20"
          title="Jump to latest messages"
        >
          {unreadCount} new message{unreadCount === 1 ? '' : 's'}
        </button>
      )}

      <button
        onClick={() => scrollToBottom('smooth')}
        className={`absolute bottom-24 right-4 md:bottom-28 md:right-6 p-3
          bg-white/95 dark:bg-gray-800/95 backdrop-blur-sm rounded-full
          shadow-lg hover:shadow-xl border border-gray-200/80 dark:border-gray-700/80
          hover:bg-sky-50 dark:hover:bg-sky-900/30 hover:border-sky-200
          z-20 group
          transition-all duration-300 ease-out
          ${
            showScrollButton
              ? 'opacity-100 scale-100 translate-y-0 pointer-events-auto'
              : 'opacity-0 scale-90 translate-y-4 pointer-events-none'
          }`}
        title="Scroll to bottom"
        aria-label="Scroll to bottom"
      >
        <ArrowDown className="w-5 h-5 text-gray-500 group-hover:text-sky-600 transition-colors" />
      </button>

      {/* Plan Progress Indicator - compact strip above input */}
      <PlanProgressIndicator
        strategy={planStrategy ?? null}
        status={planStatus ?? null}
        isPlanActive={isPlanActive}
        onOpenPanel={onOpenPlanPanel}
      />

      {/* Input Area */}
      <PromptInput
        value={chatInput}
        onChange={setChatInput}
        onSubmit={handleSubmit}
        onStop={onStop}
        status={status}
        onFilesSelected={handleFilesSelected}
        fileUploadEnabled={fileUploadEnabled}
        workspaceFiles={workspaceFiles}
        uploads={fileUpload?.uploads ?? []}
        filesSidebarOpen={filesSidebarOpen}
        sessionId={sessionId}
        userId={userId}
        onCreateSession={onCreateSession}
      />
    </div>
  );
}

export const ChatWindow = React.memo(ChatWindowInner);
